import 'tdesign-icons-web-components/esm/components/add';
import 'tdesign-icons-web-components/esm/components/chevron-down';
import 'tdesign-icons-web-components/esm/components/chevron-up';
import 'tdesign-icons-web-components/esm/components/remove';
import '../button';
import '../input';

import { isEqual, pick } from 'lodash-es';
import { bind, Component, createRef, OmiProps, signal, tag } from 'omi';

import {
  canAddNumber,
  canInputNumber,
  canReduceNumber,
  canSetValue,
  formatThousandths,
  formatUnCompleteNumber,
  getMaxOrMinValidateResult,
  getStepValue,
  InputNumberErrorType,
  largeNumberToFixed,
} from '../_common/js/input-number/number';
import classname, { getClassPrefix, getCommonClassName } from '../_util/classname';
import { convertToLightDomNode } from '../_util/lightDom';
import { StyledProps } from '../common';
import { ChangeContext, InputNumberValue, TdInputNumberProps } from './type';

const classPrefix = getClassPrefix();

export interface InputNumberProps extends TdInputNumberProps, StyledProps {}

@tag('t-input-number')
export default class InputNumber extends Component<InputNumberProps> {
  static css = `
    .${classPrefix}-input-number ${classPrefix}-button-light-dom.${classPrefix}-input-number__decrease,
    .${classPrefix}-input-number ${classPrefix}-button-light-dom.${classPrefix}-input-number__increase {
      border: none;
      transition: none;
    }

    .${classPrefix}-input-number button.${classPrefix}-input-number__decrease,
    .${classPrefix}-input-number button.${classPrefix}-input-number__increase {
      position: static;
    }
  `;

  static defaultProps = {
    allowInputOverLimit: true,
    autoWidth: false,
    decimalPlaces: undefined,
    largeNumber: false,
    max: Infinity,
    min: -Infinity,
    placeholder: '请输入',
    readonly: false,
    size: 'medium',
    status: 'default',
    step: 1,
    theme: 'row',
  };

  static propTypes = {
    allowInputOverLimit: Boolean,
    autoWidth: Boolean,
    decimalPlaces: Number,
    largeNumber: Number,
    max: Number,
    min: Number,
    placeholder: String,
    readonly: Boolean,
    size: String,
    status: String,
    step: Number,
    theme: String,
  };

  private inputRef = createRef();

  private usedValue: InputNumberValue = '';

  private error = signal<InputNumberErrorType>(undefined);

  private userInput = signal('');

  private get isControlled() {
    return Reflect.has(this.props, 'value');
  }

  private get disabledReduce() {
    const { disabled, min, largeNumber } = this.props;
    return disabled || !canReduceNumber(this.usedValue, min, largeNumber);
  }

  private get disabledAdd() {
    const { disabled, max, largeNumber } = this.props;
    return disabled || !canAddNumber(this.usedValue, max, largeNumber);
  }

  private getUserInput(value: InputNumberValue) {
    if (!value && value !== 0) {
      return '';
    }

    const { decimalPlaces, largeNumber, format } = this.props;
    let inputStr = value || value === 0 ? String(value) : '';

    const num = formatUnCompleteNumber(inputStr, {
      decimalPlaces,
      largeNumber,
      isToFixed: true,
    });
    inputStr = num || num === 0 ? String(num) : '';
    if (format) {
      inputStr = String(format(value, { fixedNumber: inputStr }));
    }
    return inputStr;
  }

  @bind
  private handleChange(value: InputNumberValue, context: ChangeContext) {
    const preUsedValue = this.usedValue;

    if (!this.isControlled) {
      this.usedValue = value;
    }
    this.props.onChange?.(value, context);

    const curUsedValue = this.usedValue;

    if (curUsedValue !== preUsedValue) {
      this.handleUsedValueChange();
      this.handleValidate();
    }

    this.update();
  }

  @bind
  private handleStepValue(op: 'add' | 'reduce') {
    const { step, max, min, largeNumber } = this.props;
    const newValue = getStepValue({
      op,
      step,
      max,
      min,
      lastValue: this.usedValue,
      largeNumber,
    });
    const overLimit = getMaxOrMinValidateResult({
      value: newValue,
      largeNumber,
      max,
      min,
    });
    return {
      overLimit,
      newValue,
    };
  }

  @bind
  private handleReduce(e: any) {
    if (this.disabledReduce || this.props.readonly) {
      return;
    }
    const r = this.handleStepValue('reduce');
    if (r.overLimit && !this.props.allowInputOverLimit) {
      return;
    }
    this.handleChange(r.newValue, { type: 'reduce', e });
  }

  @bind
  private handleAdd(e: any) {
    if (this.disabledAdd || this.props.readonly) {
      return;
    }
    const r = this.handleStepValue('add');
    if (r.overLimit && !this.props.allowInputOverLimit) {
      return;
    }
    this.handleChange(r.newValue, { type: 'add', e });
  }

  @bind
  private handleInputChange(inputValue: string) {
    const { largeNumber } = this.props;
    // 处理千分位
    const val = formatThousandths(inputValue);

    if (!canInputNumber(val, largeNumber)) {
      return;
    }

    this.userInput.value = val;

    if (largeNumber) {
      this.handleChange(val, { type: 'input', e: new InputEvent('input') });
      return;
    }

    if (canSetValue(String(val), Number(this.usedValue))) {
      const newVal = val === '' ? undefined : Number(val);
      this.handleChange(newVal, { type: 'input', e: new InputEvent('input') });
    }
  }

  @bind
  private handleKeydown(_: string, ctx: { e: KeyboardEvent }) {
    const { key } = ctx.e;

    if (key === 'ArrowUp') {
      this.handleAdd(ctx.e);
    }

    if (key === 'ArrowDown') {
      this.handleReduce(ctx.e);
    }

    this.props.onKeydown?.(this.userInput.value, ctx);
  }

  @bind
  private handleKeyUp(_: string, ctx: { e: KeyboardEvent }) {
    this.props.onKeyup?.(this.userInput.value, ctx);
  }

  @bind
  private handleKeyPress(_: string, ctx: { e: KeyboardEvent }) {
    this.props.onKeypress?.(this.userInput.value, ctx);
  }

  @bind
  private handleEnter(value: string, ctx: { e: KeyboardEvent }) {
    this.userInput.value = this.getUserInput(value);

    const { decimalPlaces, largeNumber } = this.props;
    const newValue = formatUnCompleteNumber(value, {
      decimalPlaces,
      largeNumber,
    });

    if (newValue !== value && String(newValue) !== value) {
      this.handleChange(newValue, { type: 'enter', e: ctx.e });
    }

    this.props.onEnter?.(newValue, ctx);
  }

  @bind
  private handleClear(ctx: { e: MouseEvent }) {
    this.handleChange(undefined, { type: 'clear', e: ctx.e });
    this.userInput.value = '';
  }

  @bind
  private handleFocus(_: string, ctx: { e: FocusEvent }) {
    this.userInput.value = String(this.usedValue || '');
    this.props.onFocus?.(this.usedValue, ctx);
  }

  @bind
  private handleBlur(value: string, ctx: { e: FocusEvent }) {
    const { min, max, largeNumber, allowInputOverLimit, decimalPlaces } = this.props;

    if (!allowInputOverLimit && value !== undefined) {
      const r = getMaxOrMinValidateResult({
        value: this.usedValue,
        largeNumber,
        max,
        min,
      });
      if (r === 'below-minimum') {
        this.handleChange(min, { type: 'blur', e: ctx.e });
        return;
      }
      if (r === 'exceed-maximum') {
        this.handleChange(max, { type: 'blur', e: ctx.e });
        return;
      }
    }
    const newValue = formatUnCompleteNumber(value, {
      decimalPlaces,
      largeNumber,
    });

    this.userInput.value = this.getUserInput(newValue);
    if (newValue !== this.usedValue) {
      this.handleChange(newValue, { type: 'blur', e: ctx.e });
    }

    this.props.onBlur?.(newValue, ctx);
  }

  @bind
  private handleValidate() {
    if ([undefined, '', null].includes(this.usedValue as any)) {
      return;
    }
    const { max, min, largeNumber } = this.props;
    const error = getMaxOrMinValidateResult({
      value: this.usedValue,
      max,
      min,
      largeNumber,
    });
    this.error.value = error;
    this.props.onValidate?.({ error });
  }

  @bind
  private handleUsedValueChange() {
    const inputValue = [undefined, null].includes(this.usedValue) ? '' : String(this.usedValue);

    const { largeNumber, decimalPlaces } = this.props;

    // userInput.value 为非合法数字，则表示用户正在输入，此时无需处理
    if (!largeNumber && !Number.isNaN(this.userInput.value)) {
      if (parseFloat(this.userInput.value) !== this.usedValue) {
        this.userInput.value = this.getUserInput(inputValue);
      }
      const fixedNumber = Number(largeNumberToFixed(inputValue, decimalPlaces, largeNumber));
      if (
        decimalPlaces !== undefined &&
        ![undefined, null].includes(this.usedValue) &&
        Number(fixedNumber) !== Number(this.usedValue)
      ) {
        this.handleChange(fixedNumber, { type: 'props', e: undefined });
      }
    }
    if (largeNumber) {
      const tmpUserInput = this.getUserInput(inputValue);
      this.userInput.value = tmpUserInput;
      if (
        decimalPlaces !== undefined &&
        largeNumberToFixed(inputValue, decimalPlaces, largeNumber) !== this.usedValue
      ) {
        this.handleChange(tmpUserInput, { type: 'props', e: undefined });
      }
    }
  }

  install(): void {
    this.usedValue = this.props.value || this.props.defaultValue;
    this.handleUsedValueChange();
    this.handleValidate();
  }

  receiveProps(
    props: InputNumberProps | OmiProps<InputNumberProps, any>,
    oldProps: InputNumberProps | OmiProps<InputNumberProps, any>,
  ) {
    if (
      (this.isControlled && props.value !== oldProps.value) ||
      (Reflect.has(oldProps, 'value') && !this.isControlled)
    ) {
      this.usedValue = props.value;
      this.handleUsedValueChange();
      this.handleValidate();
    }

    const value = pick(props, ['value', 'max', 'min', 'largeNumber', 'onValidate']);
    const preValue = pick(oldProps, ['value', 'max', 'min', 'largeNumber', 'onValidate']);
    if (!isEqual(value, preValue)) {
      this.handleUsedValueChange();
      this.handleValidate();
    }
  }

  render(props: OmiProps<InputNumberProps>) {
    const status = this.error.value ? 'error' : props.status;

    const { SIZE, STATUS } = getCommonClassName();

    const reduceClasses = classname(`${classPrefix}-input-number__decrease`, {
      [STATUS.disabled]: this.disabledReduce,
    });

    const addClasses = classname(`${classPrefix}-input-number__increase`, { [STATUS.disabled]: this.disabledAdd });

    const addIcon =
      props.theme === 'column'
        ? convertToLightDomNode(<t-icon-chevron-up size={props.size} />)
        : convertToLightDomNode(<t-icon-add size={props.size} />);

    const reduceIcon =
      props.theme === 'column'
        ? convertToLightDomNode(<t-icon-chevron-down size={props.size} />)
        : convertToLightDomNode(<t-icon-remove size={props.size} />);

    return (
      <div
        style={props.innerStyle}
        className={classname(
          `${classPrefix}-input-number`,
          SIZE[props.size],
          {
            [STATUS.disabled]: props.disabled,
            [`${classPrefix}-is-controls-right`]: props.theme === 'column',
            [`${classPrefix}-input-number--${props.theme}`]: props.theme,
            [`${classPrefix}-input-number--auto-width`]: props.autoWidth,
          },
          props.innerClass,
        )}
      >
        {props.theme !== 'normal' &&
          convertToLightDomNode(
            <t-button
              className={reduceClasses}
              disabled={props.disabled}
              onClick={this.handleReduce}
              variant="outline"
              shape="square"
              icon={reduceIcon}
            />,
          )}
        <t-input
          style={{ display: 'block' }}
          className={`${classPrefix}-input__wrap`}
          autocomplete="off"
          disabled={props.disabled}
          readonly={props.readonly}
          placeholder={props.placeholder}
          autoWidth={props.autoWidth}
          align={props.align || (props.theme === 'row' ? 'center' : undefined)}
          status={status}
          label={props.label}
          suffix={props.suffix}
          value={this.userInput.value}
          size={props.size}
          onChange={this.handleInputChange}
          onKeydown={this.handleKeydown}
          onKeyup={this.handleKeyUp}
          onKeypress={this.handleKeyPress}
          onEnter={this.handleEnter}
          onClear={this.handleClear}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          ref={this.inputRef}
          {...(props.inputProps || {})}
        />
        {props.theme !== 'normal' &&
          convertToLightDomNode(
            <t-button
              className={addClasses}
              disabled={props.disabled}
              onClick={this.handleAdd}
              variant="outline"
              shape="square"
              icon={addIcon}
            />,
          )}

        {props.tips && (
          <div className={classname(`${classPrefix}-input__tips`, `${classPrefix}-input__tips--${status}`)}>
            {props.tips}
          </div>
        )}
      </div>
    );
  }
}
